import { getPreferredPhone, useGetAllContacts } from "@api/contactService";
import { useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectInlineEdit from "@components/objects/ObjectInlineEdit";
import { useCreatePublicImprovement, useDeletePublicImprovement, useGetAllPublicImprovements, useUpdatePublicImprovement } from "@api/publicImprovementService";
import { formatCurrency } from "@utils/numbers";
import { formatDate } from "@utils/dates";
import { useCreateMembeshipLevel, useDeleteMembeshipLevel, useGetAllMembeshipLevels, useUpdateMembeshipLevel } from "@api/membershipLevelService";
import { useCreateTraining, useDeleteTraining, useGetAllTrainings, useUpdateTraining } from "@api/trainingService";
import { useCreateAppeal, useDeleteAppeal, useGetAllAppeals, useUpdateAppeal } from "@api/appealService";
import { useCreateCampaign, useDeleteCampaign, useGetAllCampaigns, useUpdateCampaign } from "@api/campaignService";
import { useCreatePark, useDeletePark, useGetAllParks, useUpdatePark } from "@api/parksService";
import { MapModal } from "@components/MapModal";
import { useCreatePublicParking, useDeletePublicParking, useGetAllPublicParking, useUpdatePublicParking } from "@api/publicParkingService";
import { FauxTable, FauxTableRow } from "@components/FauxTable";
import { yesNo } from "@utils/yesNo";
import { useCreateGrant, useDeleteGrant, useGetAllGrants, useUpdateGrant } from "@api/grantService";

const columns = [
    {
        id: 'name',
        numeric: false,
        disablePadding: true,
        sortable: true,
        label: 'Name',
        type: 'text',
        placeholder: 'e.g. MainStreet Improvement Grant',
        config: {
            required: true,
        },
        showLabels: true,
        cell: (row, {LinkComponent}) => {
            // const LinkComponent = view === 'deleted' ? Box : Link;
            return (<>
                <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{row.name}</Typography>
                {row.notes && <Typography fontSize={'sm'}>{row.notes}</Typography>}
            </>)
        },
        additionalEditFields: [
            {
                id: 'notes',
                placeholder: 'Notes...',
                type: 'textarea',
                tooltip: 'Notes',
                // tooltip: 'If private and public dollars were used, use two entries',
            }
        ],
      },
      {
        id: 'description',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Description',
        type: 'text',
        placeholder: 'e.g. Government grant for improving curb appeal',
        cell: (row) => row.description,
      },
      {
        id: 'grant_category_id',
        numeric: false,
        disablePadding: false,
        sortable: true,
        label: 'Category',
        type: 'relationship',
        endpoint: 'grant-categories',
        loadAll: true,
        cell: (row) => row?.category?.name ?? row?.grant_category_name ?? 'N/A', // todo: return the actual category
      },
      {
        id: 'applied_date',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Dates',
        type: 'date',
        placeholder: 'Applied Date',
        tooltip: 'Applied',
        showLabels: true,
        config: {
            required: true,
        },
        cell: (row) => {
            return (
                <FauxTable>
                    <FauxTableRow title={'Applied'}>{formatDate(row.applied_date)}</FauxTableRow>
                    <FauxTableRow title={'Awarded'}>{formatDate(row.awarded_date)}</FauxTableRow>
                </FauxTable>
            )
        },
        additionalEditFields: [
            {
                id: 'awarded_date',
                placeholder: 'Awarded Date',
                type: 'date',
                tooltip: 'Awarded',
            }
        ],
      },
      {
        id: 'value_requested',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Amount',
        type: 'number',
        startDecorator: '$',
        placeholder: 'Requested',
        tooltip: 'Requested',
        showLabels: true,
        config: {
            required: true,
        },
        cell: (row) => {
            return (
                <FauxTable>
                    <FauxTableRow title={'Requested'}>{formatCurrency(row.value_requested)}</FauxTableRow>
                    <FauxTableRow title={'Received'}>{formatCurrency(row.value)}</FauxTableRow>
                </FauxTable>
            )
        },
        additionalEditFields: [
            {
                id: 'value',
                placeholder: 'Received',
                type: 'number',
                startDecorator: '$',
                tooltip: ' Received',
            }
        ],
      },
];

const listCell = (row, {LinkComponent}) => {
    return (<>
    {row.name !== row.company_name && (
        <Box sx={{mb: 1}}>
            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>
                <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    {row.name}
                </LinkComponent>
            </Typography>
            {row.company_name && (<Typography fontSize="sm">{row.company_name}</Typography>)}
        </Box>
    )}
    {row.name === row.company_name && (
        <Box sx={{mb: 1}}>
            <Typography>
                <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    {row.company_name}
                </LinkComponent>
            </Typography>
        </Box>
        )}
    </>);
};

const listActions = (row, {view, LinkComponent, ViewAction}) => {
    if(view === 'deleted') {
        return null;
    }

    return (<>
        <Tooltip title="Email">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!row.email}
                component={'a'}
                href={`mailto:${row.email}`}
                >
                <EmailRoundedIcon />
            </IconButton>
        </Tooltip>
        <Tooltip title="Call">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!getPreferredPhone(row)}
                component={'a'}
                href={`tel:${getPreferredPhone(row)}`}
                >
                <LocalPhoneRoundedIcon />
            </IconButton>
        </Tooltip>
    </>);
};

export default function Grants() {
    return (
        <ObjectInlineEdit
            query={useGetAllGrants}
            createMutation={useCreateGrant}
            updateMutation={useUpdateGrant}
            deleteMutation={useDeleteGrant}

            type={'grant'}
            endpoint={'grants'}
            permissionType={'settings'}
            title={'Grants'}
            color={'default'}
            columns={columns}
            listCell={listCell}
            listActions={listActions}
        />
    )
}
